import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Bell, BellOff, CheckCheck, CloudOff, Folder, Keyboard, PenLine, RefreshCw, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { formatTaipeiDateTime } from "../../lib/datetime";
import {
  announceMailboxChanged,
  ARCHIVE_FOLDER,
  BACKUP_FOLDER,
  BACKUP_LABEL,
  mailbox,
  SYSTEM_SENT_FOLDER,
  SYSTEM_SENT_LABEL,
  mailErrorCode,
  mailErrorMessage,
  MAILBOX_AUTH,
  MAILBOX_DISABLED,
  MAILBOX_UNREACHABLE,
} from "../../api/mailbox";
import type { BatchAction } from "../../api/mailbox";
import type { MailBackupUsage, MailBatchResult, MailboxStatus, MailDetail, MailFolder, MailMessage, MailSearchParams } from "../../api/types";
import { MailboxSetup } from "./MailboxSetup";
import { MailList } from "./MailList";
import type { MailListRow } from "./MailList";
import { MailReader } from "./MailReader";
import type { ReplyKind } from "./MailReader";
import { MailComposer } from "./MailComposer";
import type { ComposeMode, MailComposerHandle } from "./MailComposer";
import { MailFolderNav } from "./MailFolderNav";
import { BackupPurgeDialog } from "./BackupPurgeDialog";
import { formatBackupSize, formatCount } from "./backupUsage";
import { SystemSentPanel } from "./SystemSentPanel";
import { MailThreadView } from "./MailThreadView";
import { MailBatchBar, PURGE_PROMPT, batchSummary } from "./MailBatchBar";
import { AdvancedSearch, fromParams, hasFilters } from "./AdvancedSearch";
import type { SearchFilters } from "./AdvancedSearch";
import { ScheduledList } from "./ScheduledList";
import { ShortcutHelp } from "./ShortcutHelp";
import { useMailShortcuts } from "./useMailShortcuts";
import type { MailShortcut } from "./useMailShortcuts";
import { useDesktopNotifySetting } from "./useDesktopNotify";
import { LIVE_EVENT } from "./useMailboxUnread";

// 個人信箱(個人信箱第二階段,規格 §2.4–2.6)。業主 2026-09-17:「多加上信箱功能 …
// 不可以另外再開一個網頁視窗 要直接在我們的系統上」。
//
// 讀的是**系統備份**(後端 mail_messages + 原始檔):雲端連不上、信在雲端刪掉了,照樣讀得到。
// 已讀 / 星號 / 刪除 / 搬移 / 寄信打回郵件主機。**只有本人**看得到自己的信。
//
// 頁面的幾種狀態:
// - 還沒啟用 → `MailboxSetup`(只問密碼,帳號就是登入 Email)
// - 密碼在主機上改過了(`auth_failed` 或任何動作回 409 MAILBOX_AUTH)→ 頂部紅條 + 密碼欄
// - 第一次同步還沒做完 → 「正在把你的信複製進系統…」(列表可能是空的,不能讓人以為信不見了)
// - 雲端連不上(`server_reachable: false` 或任何呼叫回 503 MAILBOX_UNREACHABLE)→
//   灰條「雲端暫時連不上,顯示的是系統備份」,會改雲端的按鈕(寫信、回覆、星號、刪除…)全部停用
//
// §2.6 的追加功能各是一個元件 / hook,這一頁只負責接線:
// - 列表「逐封 / 對話串」切換(`MailList` + `MailThreadView`);搜尋時一律逐封
// - 勾選 + 批次(`MailBatchBar`)、「全部標已讀」、快捷鍵(`useMailShortcuts`,`?` 說明)
// - 進階搜尋與捷徑(`AdvancedSearch`)、已排程(`ScheduledList`)、桌面通知開關(`useDesktopNotifySetting`)
// - 「系統寄出」(`SystemSentPanel`,資料夾 `*sent-by-system`,網址 `/mailbox?folder=*sent-by-system`):
//   系統替本人寄的信與有沒有寄成功;**沒啟用信箱也開得了**(與設定提示並列),它不碰 IMAP / 金鑰
// - 手機(< 768px,Tailwind `md`):單欄流程 資料夾 → 列表 → 讀信,各有返回鍵
// - 列印只印讀信區(`PRINT_CSS` + 讀信區的 `nq-mail-print`)

const INBOX = "INBOX";
const TRASH = "INBOX.Trash";
const SENT = "INBOX.Sent";
/** 新信推播連來時,合併成一次重抓的等待時間。 */
const LIVE_REFRESH_MS = 800;
/** 搜尋框停手多久才送出。 */
const SEARCH_DEBOUNCE_MS = 300;
/** 第一次同步還沒做完時,多久回頭看一次進度。 */
const INITIAL_SYNC_POLL_MS = 10_000;
/** 動作回 `sync_pending`(背景正在同步):過一下再重抓一次列表。 */
const PENDING_RELOAD_MS = 5_000;
/** 放棄寫到一半的信之前問的那一句(點別封、再按寫信、關閉 / 取消都一樣)。 */
const DISCARD_PROMPT = "要放棄正在寫的信嗎?";

const FALLBACK_FOLDERS: MailFolder[] = [{ name: INBOX, label: "收件匣", total: 0, unread: 0 }];

/** 手機版面(單欄)?與 Tailwind 的 `md`(768px)同一條線。 */
function narrowScreen(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches;
}

/** 列印:只印讀信區(其餘整頁藏起來;讀信區裡的按鈕另有 `print:hidden`)。 */
const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  .nq-mail-print, .nq-mail-print * { visibility: visible !important; }
  .nq-mail-print { position: absolute; left: 0; top: 0; width: 100%; border: 0 !important; }
}`;

/**
 * 大螢幕(lg)時整頁高度 = 視窗高 − 頂列 − 內容區上下 padding,整頁不捲,三欄各自捲。
 * 頂列 = 一列 32px 的按鈕(h-8)+ py-2 上下共 16px + 1px 底線 = 49px(3.0625rem);
 * 內容區 py-4 上下共 2rem(見 layout/AppShell.tsx、layout/TopBar.tsx)。改了頂列要跟著改這裡。
 */
const PAGE_HEIGHT = "lg:h-[calc(100dvh-3.0625rem-2rem)]";

type ListView = "messages" | "threads";
type MobilePane = "folders" | "list";
interface OpenThread {
  key: string;
  items: MailMessage[] | null;
}

export function MailboxPage() {
  const toast = useToast();
  const [urlParams, setUrlParams] = useSearchParams();
  const [status, setStatus] = useState<MailboxStatus | null>(null);
  const [statusFailed, setStatusFailed] = useState(false);
  // 呼叫途中撞到的狀況(狀態那一支是之前問的,可能已經過時)
  const [unreachable, setUnreachable] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);

  const [folders, setFolders] = useState<MailFolder[]>([]);
  // 網址 ?folder=*sent-by-system 直接開在「系統寄出」(舊的 /outbox 轉到這裡);其餘一律從收件匣開始
  const [folder, setFolder] = useState(urlParams.get("folder") === SYSTEM_SENT_FOLDER ? SYSTEM_SENT_FOLDER : INBOX);
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState<SearchFilters>({});
  const [view, setView] = useState<ListView>("messages");
  const [items, setItems] = useState<MailListRow[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [checked, setChecked] = useState<Set<number>>(() => new Set());
  const [cursorId, setCursorId] = useState<number | null>(null);
  const [batchNotice, setBatchNotice] = useState<string | null>(null);

  const [openId, setOpenId] = useState<number | null>(null);
  const [thread, setThread] = useState<OpenThread | null>(null);
  const [detail, setDetail] = useState<MailDetail | null>(null);
  const [imagesAllowed, setImagesAllowed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [compose, setCompose] = useState<ComposeMode | null>(null);
  // 每開一次寫信就換一個 key:再按「寫信」時真的換一個全新的寫信區(不沿用上一封打到一半的字)
  const [composeSeq, setComposeSeq] = useState(0);
  const composerRef = useRef<MailComposerHandle | null>(null);
  // 寫信那一塊有沒有被動過(見 MailComposer 的 onDirtyChange);everDirty = 這封曾經動過(改回原樣也算)
  const composeDirty = useRef(false);
  const composeEverDirty = useRef(false);
  // 寄出了但有收件人被郵件主機拒收:留在頁面上,關掉才消失(提示會自己消失,容易漏看)
  const [refusedWarning, setRefusedWarning] = useState<string[] | null>(null);

  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [scheduledRefresh, setScheduledRefresh] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mobilePane, setMobilePane] = useState<MobilePane>("list");
  const searchRef = useRef<HTMLInputElement | null>(null);
  const notify = useDesktopNotifySetting();
  const foldersPaneRef = useRef<HTMLElement | null>(null);
  const listPaneRef = useRef<HTMLDivElement | null>(null);
  const readerPaneRef = useRef<HTMLDivElement | null>(null);

  // 現在打開的是哪一封。**同步**記在 ref:讀信的回應晚到時拿它比對,
  // 不是這一封的回應一律丟掉(先開 A 再開 B、A 比較晚回來,不可以蓋掉 B)。
  const openIdRef = useRef<number | null>(null);
  const selectMessage = (id: number | null) => {
    openIdRef.current = id;
    setOpenId(id);
  };

  const alive = useRef(true);
  const timers = useRef<number[]>([]);
  useEffect(() => {
    alive.current = true;
    const pending = timers.current;
    return () => {
      alive.current = false;
      pending.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const enabled = status?.enabled === true;
  const cloudDown = unreachable || (status !== null && !status.server_reachable);
  const needsPassword = authFailed || status?.auth_failed === true;
  /** 會改雲端的按鈕要不要停用。 */
  const cloudDisabled = cloudDown || needsPassword;
  const searching = q !== "" || hasFilters(filters);
  /** 在「系統備份」:唯讀的檔案庫(含雲端已刪除),不能刪 / 搬 / 批次 / 標已讀,也沒有對話串。 */
  const inBackup = folder === BACKUP_FOLDER;
  /** 在「系統寄出」:系統替本人寄的信,自己一組列表與讀信區(`SystemSentPanel`),不走信箱列表。 */
  const inSystemSent = folder === SYSTEM_SENT_FOLDER;
  /** 對話串模式(搜尋時一律逐封:對話串的端點不吃搜尋條件;系統備份也只有逐封)。 */
  const threadMode = view === "threads" && !searching && !inBackup;

  const loadStatus = useCallback(async () => {
    try {
      const got = await mailbox.status();
      if (!alive.current) return;
      setStatus(got);
      setStatusFailed(false);
    } catch {
      if (alive.current) setStatusFailed(true);
    }
  }, []);

  /**
   * 呼叫失敗的共同處理。回傳 true 表示已經變成畫面上的狀態列(不必再跳提示)。
   */
  const handleError = useCallback(
    (err: unknown, quiet = false): boolean => {
      const code = mailErrorCode(err);
      if (code === MAILBOX_UNREACHABLE) {
        setUnreachable(true);
        return true;
      }
      if (code === MAILBOX_AUTH) {
        setAuthFailed(true);
        return true;
      }
      if (code === MAILBOX_DISABLED) {
        void loadStatus();
        return true;
      }
      if (!quiet) toast.push(mailErrorMessage(err), "error");
      return false;
    },
    [loadStatus, toast],
  );

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const [usage, setUsage] = useState<MailBackupUsage | null>(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const loadUsage = useCallback(async () => {
    try {
      const got = await mailbox.backupUsage();
      if (alive.current && got && typeof got.total_bytes === "number") setUsage(got);
    } catch {
      /* 用量只是參考,讀不到就不顯示 */
    }
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const got = await mailbox.folders();
      if (alive.current) setFolders(Array.isArray(got) ? got : []);
    } catch (err) {
      handleError(err, true);
    }
  }, [handleError]);

  // 列表。`before` 有值 = 往下載下一頁(接在後面)。
  const listToken = useRef(0);
  const listBusy = useRef(0); // 進行中的非靜默載入數(含載入更多)
  // 本機拿掉列表上的信(刪除、封存、批次)就加一:這之前發出的靜默重抓,回應是「拿掉之前」的快照,
  // 合併進來會把剛刪的信帶回來,所以一律丟掉(之後再抓一次)
  const quietGen = useRef(0);
  const loadList = useCallback(
    async (before: number | null = null, quiet = false) => {
      if (inSystemSent) return; // 「系統寄出」有自己的列表
      // 靜默重抓不換 token(否則會把進行中的載入結果丟掉、轉圈停不下來)
      const token = quiet ? listToken.current : before === null ? ++listToken.current : listToken.current;
      const gen = quietGen.current;
      if (!quiet) {
        listBusy.current += 1;
        setListLoading(true);
      }
      try {
        const { all_folders: allFolders, ...rest } = filters;
        const page = threadMode
          ? await mailbox.threads(folder, before)
          : await mailbox.messages({ ...rest, folder: inBackup ? BACKUP_FOLDER : allFolders ? "*" : folder, q, before });
        if (!alive.current || token !== listToken.current) return;
        if (quiet && gen !== quietGen.current) {
          void loadListRef.current(null, true); // 快照過時了:丟掉,重抓一次
          return;
        }
        const rows: MailListRow[] = Array.isArray(page?.items) ? page.items : [];
        if (quiet) {
          // 推播來的靜默更新:只換第一頁,已經往下載入的舊頁留著,勾選、游標、捲動都不動
          const prev = itemsRef.current;
          const lastId = rows.length ? rows[rows.length - 1].id : null;
          const at = lastId === null ? -1 : prev.findIndex((one) => one.id === lastId);
          const seen = new Set(rows.map((one) => one.id));
          // 第一頁最後一列在舊列表裡 → 它後面的舊頁原封保留;不在(新信多過一頁)→ 舊的全留、去重
          const tail = at >= 0 ? prev.slice(at + 1) : prev.filter((one) => !seen.has(one.id));
          const merged = [...rows, ...tail];
          setItems(merged);
          if (tail.length === 0) setNextBefore(page?.next_before ?? null);
          setChecked((cur) => {
            const ids = new Set(merged.map((one) => one.id));
            const keep = [...cur].filter((id) => ids.has(id));
            return keep.length === cur.size ? cur : new Set(keep);
          });
          return;
        }
        setItems((prev) => (before === null ? rows : [...prev, ...rows]));
        setNextBefore(page?.next_before ?? null);
        if (before === null) {
          // 換了一批信:之前勾的、走到的不一定還在
          setChecked(new Set());
          setCursorId(null);
        }
      } catch (err) {
        if (token === listToken.current) handleError(err, quiet);
      } finally {
        if (!quiet) listBusy.current -= 1;
        if (!quiet && alive.current && token === listToken.current) setListLoading(false);
      }
    },
    [folder, q, filters, threadMode, inBackup, inSystemSent, handleError],
  );
  // 延後才跑的重抓(同步回來、動作後、寄出後)一律走這個 ref,拿到的是**當下**的搜尋字與資料夾 ——
  // 直接呼叫閉包裡的 loadList 會用舊的 q,把剛搜出來的結果蓋回沒篩過的列表。
  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // 新信推播(`useNotifications` 廣播 nq-live):停手 ~800ms 後靜默重抓資料夾與目前這一頁列表。
  // 不動開著的信、寫信區、勾選;連來好幾次只重抓一次。
  useEffect(() => {
    if (!enabled) return;
    let timer = 0;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (!alive.current) return;
        if (listBusy.current > 0) {
          schedule(); // 列表正在載入(含載入更多):等它載完再重抓,不丟掉這次推播
          return;
        }
        void loadFolders();
        void loadUsage();
        void loadListRef.current(null, true);
      }, LIVE_REFRESH_MS);
    };
    const onLive = schedule;
    window.addEventListener(LIVE_EVENT, onLive);
    return () => {
      window.removeEventListener(LIVE_EVENT, onLive);
      window.clearTimeout(timer);
    };
  }, [enabled, loadFolders, loadUsage]);

  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  // 搜尋:停手 300ms 才送
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!enabled) return;
    void loadList();
  }, [enabled, loadList]);

  useEffect(() => {
    if (!enabled) return;
    void loadFolders();
    void loadUsage();
  }, [enabled, loadFolders, loadUsage]);

  // 打開信箱頁(與換資料夾)時同步目前這個資料夾(規格:打開信箱頁時同步目前資料夾)
  useEffect(() => {
    if (!enabled || folder === BACKUP_FOLDER || folder === SYSTEM_SENT_FOLDER) return; // 這兩個不是雲端資料夾,沒有東西可同步
    let cancelled = false;
    mailbox
      .sync(folder)
      .then((got) => {
        if (cancelled || !alive.current) return;
        setUnreachable(false);
        if (got && typeof got.enabled === "boolean") {
          const { sync_pending: _pending, ...rest } = got;
          void _pending;
          setStatus(rest);
        }
        void loadFolders();
        void loadListRef.current();
      })
      .catch((err: unknown) => {
        if (!cancelled) handleError(err, true);
      });
    return () => {
      cancelled = true;
    };
    // 只在啟用與換資料夾時同步;loadList / loadFolders 換了(搜尋字變了)不必再同步
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, folder]);

  // 第一次同步還沒做完:隔一陣子回頭看進度,做完就重抓
  const initialDone = status?.initial_sync_done !== false;
  useEffect(() => {
    if (!enabled || initialDone) return;
    const timer = window.setInterval(() => {
      void loadStatus();
      void loadFolders();
      void loadListRef.current();
    }, INITIAL_SYNC_POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, initialDone, loadStatus, loadFolders]);

  const patchDetail = (id: number, patch: Partial<MailDetail>) =>
    setDetail((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));

  const patchItem = (row: Partial<MailMessage> & { id: number }) => {
    setItems((prev) => prev.map((one) => (one.id === row.id ? { ...one, ...row } : one)));
    setDetail((prev) => (prev && prev.id === row.id ? { ...prev, ...row } : prev));
    setThread((prev) =>
      prev?.items ? { ...prev, items: prev.items.map((one) => (one.id === row.id ? { ...one, ...row } : one)) } : prev,
    );
  };

  /** 這幾封從目前的列表(與開著的對話串)拿掉,勾選也一併清掉。 */
  const dropRows = (ids: number[]) => {
    const gone = new Set(ids);
    quietGen.current += 1;
    setItems((prev) => prev.filter((one) => !gone.has(one.id)));
    setThread((prev) => (prev?.items ? { ...prev, items: prev.items.filter((one) => !gone.has(one.id)) } : prev));
    setChecked((cur) => {
      const keep = [...cur].filter((id) => !gone.has(id));
      return keep.length === cur.size ? cur : new Set(keep);
    });
  };

  /** 讀一封信。回來時人已經換到別封了就丟掉(回 null),不蓋掉畫面上那一封。 */
  const fetchDetail = useCallback(
    async (id: number, images: boolean) => {
      try {
        const got = await mailbox.message(id, images);
        if (!alive.current || openIdRef.current !== id) return null;
        setDetail(got);
        return got;
      } catch (err) {
        if (openIdRef.current === id) handleError(err);
        return null;
      }
    },
    [handleError],
  );

  /**
   * 離開寫信那一塊(點別封、再按寫信、寫信區的關閉 / 取消、快捷鍵)。動過就先問;
   * 確定放棄就連自動存的草稿一起刪掉。回傳 false = 本人選擇留下。
   */
  const leaveCompose = (): boolean => {
    if (!compose) return true;
    if (composeDirty.current && !window.confirm(DISCARD_PROMPT)) return false;
    // 動過(或真的存過草稿)才需要刪草稿;沒動過的寫信區直接關
    if (composeEverDirty.current || composerRef.current?.hasDraft()) void composerRef.current?.discard();
    composeDirty.current = false;
    composeEverDirty.current = false;
    setCompose(null);
    return true;
  };

  /** 從「系統寄出」(自己的讀信區)轉去要用信箱讀信區的功能(寫信、已排程):先回收件匣。 */
  const leaveSystemSent = () => {
    if (folder !== SYSTEM_SENT_FOLDER) return;
    setFolder(INBOX);
    setItems([]);
    setNextBefore(null);
  };

  const startCompose = (mode: ComposeMode) => {
    if (!leaveCompose()) return;
    leaveSystemSent();
    composeDirty.current = false;
    composeEverDirty.current = false;
    setScheduledOpen(false);
    setComposeSeq((n) => n + 1);
    setCompose(mode);
  };

  const closeReader = () => {
    selectMessage(null);
    setDetail(null);
    setThread(null);
  };

  /** 讀一封(逐封模式從列表點、對話串裡展開)。 */
  const showMessage = async (one: MailMessage) => {
    selectMessage(one.id);
    setDetail(null);
    setImagesAllowed(false);
    const got = await fetchDetail(one.id, false);
    if (!got || got.seen || got.deleted_on_server || cloudDisabled || inBackup) return;
    // 打開即在雲端標已讀(規格 §2.4)。失敗不打擾:沒標成也照樣讀得到
    try {
      await mailbox.flags(got.id, { seen: true });
      if (!alive.current) return;
      patchItem({ id: got.id, seen: true });
      setFolders((prev) =>
        prev.map((f) => (f.name === got.folder ? { ...f, unread: Math.max(0, f.unread - 1) } : f)),
      );
      announceMailboxChanged();
    } catch (err) {
      handleError(err, true);
    }
  };

  const threadToken = useRef(0);
  const openThread = async (row: MailListRow) => {
    const key = row.thread_key ?? "";
    const token = ++threadToken.current;
    setThread({ key, items: null });
    selectMessage(row.id);
    setDetail(null);
    try {
      const got = await mailbox.thread(key);
      if (!alive.current || token !== threadToken.current) return;
      const rows = Array.isArray(got?.items) ? got.items : [];
      setThread({ key, items: rows });
      await showMessage(rows[rows.length - 1] ?? row); // 預設展開最新一封
    } catch (err) {
      if (token !== threadToken.current) return;
      if (mailErrorCode(err) === "MAILBOX_NOT_FOUND") {
        // 這一串的信都刪光了(別處刪的、列表還沒更新):從列表拿掉,不當成錯誤
        dropRows([row.id]);
        closeReader();
        return;
      }
      handleError(err);
    }
  };

  const openRow = (row: MailListRow) => {
    if (!leaveCompose()) return;
    setScheduledOpen(false);
    setCursorId(row.id);
    if (threadMode && row.thread_key) {
      void openThread(row);
      return;
    }
    threadToken.current += 1;
    setThread(null);
    void showMessage(row);
  };

  const afterAction = (pending: boolean | undefined) => {
    void loadFolders();
    announceMailboxChanged();
    if (pending) {
      later(() => {
        if (!alive.current) return;
        void loadFolders();
        void loadListRef.current();
      }, PENDING_RELOAD_MS);
    }
  };

  /** 讀信區的動作能不能做:雲端連不上、這封在雲端已刪除、正在做別的,都不行(快捷鍵也一樣擋)。 */
  const canChange = (one: MailDetail | null): one is MailDetail =>
    one !== null && !busy && !cloudDisabled && !inBackup && !one.deleted_on_server;

  const toggle = async (change: { seen?: boolean; flagged?: boolean }) => {
    if (!canChange(detail)) return;
    setBusy(true);
    try {
      const got = await mailbox.flags(detail.id, change);
      patchItem({ id: detail.id, ...change });
      setUnreachable(false);
      afterAction(got?.sync_pending);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!canChange(detail)) return;
    if (
      detail.folder === TRASH &&
      !window.confirm(PURGE_PROMPT)
    ) {
      return;
    }
    setBusy(true);
    try {
      const got = await mailbox.trash(detail.id);
      dropRows([detail.id]); // 刪掉的信馬上從列表消失(垃圾桶那份稍後由同步帶回來)
      setUnreachable(false);
      toast.push(detail.folder === TRASH ? "已從雲端刪除,系統備份還留著" : "已移到垃圾桶", "success");
      closeReader();
      afterAction(got?.sync_pending);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!canChange(detail) || detail.folder === ARCHIVE_FOLDER) return;
    setBusy(true);
    try {
      const got = await mailbox.move(detail.id, ARCHIVE_FOLDER);
      dropRows([detail.id]);
      setUnreachable(false);
      toast.push("已封存", "success");
      closeReader();
      afterAction(got?.sync_pending);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const chooseFolder = (name: string) => {
    setChecked(new Set()); // 換了一批信:看不到的勾選一律清掉,不能被批次動作碰到
    setMobilePane("list");
    setScheduledOpen(false);
    // 網址跟著畫面走:在「系統寄出」就帶 ?folder=*sent-by-system(重新整理仍在這裡),選了別的就拿掉
    // (否則重新整理會跳回系統寄出,指令面板再導一次同樣的網址也不會有反應)
    if ((urlParams.get("folder") === SYSTEM_SENT_FOLDER) !== (name === SYSTEM_SENT_FOLDER)) {
      setUrlParams(name === SYSTEM_SENT_FOLDER ? { folder: SYSTEM_SENT_FOLDER } : {}, { replace: true });
    }
    if (name === folder) return;
    setFolder(name);
    setItems([]);
    setNextBefore(null);
    closeReader();
  };

  // 已經在信箱頁時又導到 ?folder=*sent-by-system(指令面板、舊的 /outbox 連結):切過去
  const wantSystemSent = urlParams.get("folder") === SYSTEM_SENT_FOLDER;
  useEffect(() => {
    if (wantSystemSent) chooseFolder(SYSTEM_SENT_FOLDER);
    // 只在網址的 folder 變成「系統寄出」時切;之後使用者自己點別的資料夾不能被它拉回來
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantSystemSent]);

  const afterBatch = (result: MailBatchResult, action?: BatchAction) => {
    const summary = batchSummary(result);
    if (summary) {
      setBatchNotice(summary);
    } else {
      setBatchNotice(null);
      toast.push("完成了", "success");
    }
    setChecked(new Set());
    // 刪除 / 搬移成功的信,在這個資料夾裡已經不在了:馬上拿掉,不等重抓
    if ((action === "trash" || action === "move") && Array.isArray(result?.done)) dropRows(result.done);
    void loadListRef.current();
    afterAction(result?.sync_pending);
  };

  const markAllRead = async () => {
    try {
      const got = await mailbox.markAllRead(folder);
      toast.push(got?.marked ? `已把 ${got.marked} 封標成已讀` : "這個資料夾沒有未讀的信", "success");
      void loadListRef.current();
      afterAction(got?.sync_pending);
    } catch (err) {
      handleError(err);
    }
  };

  const replay = (params: MailSearchParams) => {
    const got = fromParams(params);
    setChecked(new Set());
    setQuery(got.q);
    setQ(got.q);
    setFilters(got.filters);
    if (got.folder) chooseFolder(got.folder);
  };

  const moveCursor = (delta: number) => {
    if (items.length === 0) return;
    const from = items.findIndex((one) => one.id === (cursorId ?? openId));
    const next = from < 0 ? (delta > 0 ? 0 : items.length - 1) : Math.min(items.length - 1, Math.max(0, from + delta));
    const id = items[next].id;
    setCursorId(id);
    document.querySelector<HTMLElement>(`[data-mail-row="${id}"]`)?.focus();
  };

  useMailShortcuts((action: MailShortcut) => {
    if (action === "help") return setHelpOpen(true);
    if (action === "search") return searchRef.current?.focus();
    if (action === "compose") {
      if (!cloudDisabled) startCompose({ kind: "new" });
      return;
    }
    if (compose) return; // 寫信中:其餘的鍵不碰讀信區
    switch (action) {
      case "next":
        return moveCursor(1);
      case "prev":
        return moveCursor(-1);
      case "open": {
        const row = items.find((one) => one.id === cursorId);
        if (row) openRow(row);
        return;
      }
      case "back":
        setScheduledOpen(false);
        return closeReader();
      case "reply":
      case "replyAll":
      case "forward":
        if (detail && detail.id === openId && !cloudDisabled) startCompose({ kind: action, original: detail });
        return;
      case "star":
        if (detail) void toggle({ flagged: !detail.flagged });
        return;
      case "archive":
        return void archive();
      case "delete":
        return void remove();
    }
  }, enabled && !inSystemSent);

  const enableNotify = async () => {
    const got = await notify.enable();
    if (got === "on") toast.push("有新信、而且你不在這個分頁時,會跳桌面通知", "success");
    else if (got === "denied") toast.push("瀏覽器封鎖了通知,要在瀏覽器的網站設定裡允許", "error");
  };

  // 手機:讀信 / 寫信 / 已排程開著就只顯示右欄;否則依 mobilePane 顯示資料夾或列表
  const pane: MobilePane | "reader" = compose || scheduledOpen || openId !== null ? "reader" : mobilePane;

  // 手機換欄時把焦點帶過去(看不見的欄裡留著焦點,鍵盤與報讀軟體就迷路了):
  // 進讀信區 → 那一欄的返回鍵(內容從「載入中」換成信時,焦點跟著掉到 body,也再帶一次);
  // 回列表 → 剛剛打開的那一列;回資料夾 → 目前的資料夾。
  const lastPane = useRef(pane);
  useEffect(() => {
    const before = lastPane.current;
    lastPane.current = pane;
    if (!narrowScreen()) return;
    const active = document.activeElement;
    if (pane === "reader") {
      const box = readerPaneRef.current;
      const lost = active === null || active === document.body;
      if (box && (before !== pane || lost) && !box.contains(active)) {
        box.querySelector<HTMLElement>("[data-back]")?.focus();
      }
      return;
    }
    if (before === pane) return;
    if (pane === "list") {
      const list = listPaneRef.current;
      const row = cursorId !== null ? list?.querySelector<HTMLElement>(`[data-mail-row="${cursorId}"]`) : null;
      (row ?? list?.querySelector<HTMLElement>("[data-pane-start]"))?.focus();
    } else {
      const nav = foldersPaneRef.current;
      (nav?.querySelector<HTMLElement>('[aria-current="page"]') ?? nav?.querySelector<HTMLElement>("button"))?.focus();
    }
  });

  // ---------- 畫面 ----------

  if (status === null) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <h2 className="m-0 text-xl font-semibold text-[var(--text)]">信箱</h2>
        {statusFailed ? (
          <div className="flex flex-wrap items-center gap-2">
            <p role="alert" className="m-0 text-sm text-[var(--danger)]">
              讀不到信箱狀態,請稍後再試。
            </p>
            <Button size="sm" onClick={() => void loadStatus()}>
              再試一次
            </Button>
          </div>
        ) : (
          <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
        )}
      </div>
    );
  }

  if (!enabled) {
    // 沒啟用信箱:設定提示照舊,左邊多一個「系統寄出」 —— 系統替你寄的信不需要信箱也看得到
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <h2 className="m-0 text-xl font-semibold text-[var(--text)]">信箱</h2>
        <div className="flex flex-col gap-4 lg:flex-row lg:gap-0">
          <div
            data-mail-pane="folders"
            className={
              "w-full shrink-0 flex-col gap-3 lg:w-56 lg:border-r lg:border-[var(--border)] lg:pr-3 " +
              (!inSystemSent || mobilePane === "folders" ? "flex" : "hidden md:flex")
            }
          >
            <MailFolderNav
              ref={foldersPaneRef}
              folders={[]}
              current={folder}
              scheduledOpen={false}
              className="w-full"
              showBackup={false}
              onChoose={chooseFolder}
            />
            {inSystemSent ? (
              <Button size="sm" variant="ghost" onClick={() => chooseFolder(INBOX)}>
                啟用信箱
              </Button>
            ) : null}
          </div>
          {inSystemSent ? (
            <SystemSentPanel mobilePane={mobilePane} onShowFolders={() => setMobilePane("folders")} />
          ) : (
            <div className="min-w-0 flex-1 lg:pl-3">
              <MailboxSetup
                mode="setup"
                email={status.email}
                onEnabled={(got) => {
                  setUnreachable(false);
                  setAuthFailed(false);
                  setStatus(got);
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  const shownFolders = folders.length > 0 ? folders : FALLBACK_FOLDERS;
  const folderLabel = inBackup ? BACKUP_LABEL : inSystemSent ? SYSTEM_SENT_LABEL : (shownFolders.find((f) => f.name === folder)?.label ?? folder);
  /** 讀信區還在載入:手機上也要有返回鍵。 */
  const loadingWithBack = (
    <div className="flex items-center justify-between gap-2">
      <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      <Button size="sm" variant="ghost" data-back="" icon={<ArrowLeft aria-hidden="true" size={16} />} onClick={closeReader}>
        回列表
      </Button>
    </div>
  );

  const reader =
    detail && detail.id === openId ? (
      <MailReader
        detail={detail}
        imagesAllowed={imagesAllowed}
        cloudDisabled={cloudDisabled}
        readOnly={inBackup}
        busy={busy}
        onBack={closeReader}
        onShowImages={() => {
          setImagesAllowed(true);
          void fetchDetail(detail.id, true);
        }}
        onReply={(kind: ReplyKind) => startCompose({ kind, original: detail })}
        onToggleFlag={() => void toggle({ flagged: !detail.flagged })}
        onToggleSeen={() => void toggle({ seen: !detail.seen })}
        onDelete={() => void remove()}
        onArchive={() => void archive()}
        followupDue={detail.followup_due ?? null}
        onFollowupChange={(due) => patchDetail(detail.id, { followup_due: due })}
      />
    ) : openId !== null ? (
      loadingWithBack
    ) : null;

  return (
    <div className={"flex w-full flex-col gap-3 lg:min-h-0 " + PAGE_HEIGHT}>
      <style>{PRINT_CSS}</style>
      {/* 工具列與橫幅:大螢幕固定在欄位上方,不跟著欄位捲(自己太高時才自己捲)。 */}
      <div className="flex flex-col gap-3 lg:max-h-[45%] lg:shrink-0 lg:overflow-y-auto">
      <div data-mail-toolbar="" className="flex flex-wrap items-start gap-x-4 gap-y-2 xl:flex-nowrap">
        <div className="flex min-w-0 flex-col lg:w-48 lg:shrink-0">
          <h2 className="m-0 text-xl font-semibold text-[var(--text)]">信箱</h2>
          <span className="text-xs text-[var(--text-3)]">
            {status.email}
            {status.last_sync_at ? ` · 上次同步 ${formatTaipeiDateTime(status.last_sync_at)}` : ""}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-start gap-2 max-xl:basis-full max-xl:order-last xl:flex-nowrap">
          {/* 「系統寄出」不支援搜尋(列表就是你自己的那幾封),整排藏起來免得以為它有用 */}
          {inSystemSent ? null : (
          <>
          <div className="min-w-[14rem] flex-1">
            <Input
              ref={searchRef}
              label="搜尋"
              hint={inBackup ? "主旨、寄件人、收件人;含雲端已刪除的信" : "主旨、寄件人、收件人"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="pt-6">
            <AdvancedSearch value={filters} q={q} folder={folder} onApply={setFilters} onReplay={replay} />
          </div>
          </>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2 lg:ml-0 lg:shrink-0">
          {notify.state === "off" ? (
            <Button size="sm" variant="ghost" icon={<Bell aria-hidden="true" size={16} />} onClick={() => void enableNotify()}>
              開啟桌面通知
            </Button>
          ) : notify.state === "on" ? (
            <Button size="sm" variant="ghost" icon={<BellOff aria-hidden="true" size={16} />} onClick={notify.disable}>
              關閉桌面通知
            </Button>
          ) : notify.state === "denied" ? (
            <span className="text-xs text-[var(--text-3)]">瀏覽器封鎖了桌面通知</span>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            aria-label="快捷鍵說明"
            icon={<Keyboard aria-hidden="true" size={16} />}
            onClick={() => setHelpOpen(true)}
            className="hidden md:inline-flex"
          />
        </div>
      </div>

      {needsPassword ? (
        <MailboxSetup
          mode="reauth"
          onEnabled={(got) => {
            setAuthFailed(false);
            setUnreachable(false);
            setStatus(got);
            toast.push("密碼已更新,馬上重新同步", "success");
            void loadFolders();
            void loadListRef.current();
          }}
        />
      ) : null}

      {refusedWarning ? (
        <div
          role="alert"
          aria-label="寄件警告"
          className="flex items-start gap-2 rounded-[var(--radius)] border border-[var(--warn)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)]"
        >
          <span className="min-w-0 flex-1">
            信寄出了,但這幾位郵件主機不收,他們沒有收到:<strong>{refusedWarning.join("、")}</strong>。
            請檢查信箱有沒有打錯。
          </span>
          <Button
            size="sm"
            variant="ghost"
            aria-label="關閉寄件警告"
            icon={<X aria-hidden="true" size={14} />}
            onClick={() => setRefusedWarning(null)}
          />
        </div>
      ) : null}

      {batchNotice ? (
        <div
          role="alert"
          aria-label="批次結果"
          className="flex items-start gap-2 rounded-[var(--radius)] border border-[var(--warn)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)]"
        >
          <span className="min-w-0 flex-1">{batchNotice}</span>
          <Button
            size="sm"
            variant="ghost"
            aria-label="關閉批次結果"
            icon={<X aria-hidden="true" size={14} />}
            onClick={() => setBatchNotice(null)}
          />
        </div>
      ) : null}

      {cloudDown ? (
        <p
          role="status"
          className="m-0 flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--text-2)]"
        >
          <CloudOff aria-hidden="true" size={16} className="shrink-0" />
          雲端暫時連不上,顯示的是系統備份
        </p>
      ) : null}

      {!initialDone ? (
        <p
          role="status"
          className="m-0 flex items-center gap-2 rounded-[var(--radius)] border border-[var(--accent)] bg-[var(--accent-soft)] px-3 py-2 text-sm text-[var(--text-2)]"
        >
          <RefreshCw aria-hidden="true" size={16} className="shrink-0 animate-spin" />
          正在把你的信複製進系統…
          {status?.initial_sync && status.initial_sync.folders_total > 0
            ? `(${status.initial_sync.folders_done}/${status.initial_sync.folders_total} 個資料夾)`
            : ""}
          信多的話要一陣子,複製好的(最新的先)會先列出來。
        </p>
      ) : null}
      </div>

      <div className="flex flex-col gap-4 lg:min-h-0 lg:flex-1 lg:flex-row lg:gap-0">
        <div
          data-mail-pane="folders"
          className={
            "w-full shrink-0 flex-col gap-3 lg:min-h-0 lg:w-56 lg:overflow-y-auto lg:border-r lg:border-[var(--border)] lg:pr-3 " +
            (pane === "folders" ? "flex" : "hidden md:flex")
          }
        >
        <Button
          variant="primary"
          size="md"
          className="w-full md:w-auto lg:w-full lg:justify-center"
          icon={<PenLine aria-hidden="true" size={18} />}
          disabled={cloudDisabled}
          onClick={() => startCompose({ kind: "new" })}
        >
          寫信
        </Button>
        <MailFolderNav
          ref={foldersPaneRef}
          folders={shownFolders}
          current={folder}
          scheduledOpen={scheduledOpen}
          className="w-full"
          backupUsage={usage}
          onChoose={chooseFolder}
          onScheduled={() => {
            if (!leaveCompose()) return;
            leaveSystemSent();
            closeReader();
            setScheduledOpen(true);
          }}
        />
        </div>

        {inSystemSent ? (
          <SystemSentPanel mobilePane={mobilePane === "folders" ? "folders" : "list"} onShowFolders={() => setMobilePane("folders")} />
        ) : (
        <>
        <div
          ref={listPaneRef}
          data-mail-pane="list"
          className={
            "min-w-0 flex-col gap-2 lg:min-h-0 lg:w-[28rem] lg:shrink-0 lg:border-r lg:border-[var(--border)] lg:px-3 2xl:w-[32rem] " +
            (pane === "list" ? "flex" : "hidden md:flex")
          }
        >
          <div className="flex items-center gap-2 md:hidden">
            <Button
              size="sm"
              variant="ghost"
              data-pane-start=""
              icon={<Folder aria-hidden="true" size={16} />}
              onClick={() => setMobilePane("folders")}
            >
              資料夾
            </Button>
            <span className="min-w-0 flex-1 text-sm font-medium text-[var(--text)]">{folderLabel}</span>
            {/* 手機只顯示列表時看不到資料夾欄裡的「寫信」,所以這裡放一顆(只有圖示,名稱不同,免得與大按鈕重名)。 */}
            <Button
              size="sm"
              variant="primary"
              aria-label="寫新信"
              icon={<PenLine aria-hidden="true" size={16} />}
              disabled={cloudDisabled}
              onClick={() => startCompose({ kind: "new" })}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {inBackup ? (
              <span className="text-xs text-[var(--text-3)]">全部備份的信(含雲端已刪除),只能讀。</span>
            ) : (
            <div role="group" aria-label="列表顯示" className="inline-flex gap-1">
              <Button size="sm" variant={view === "messages" ? "secondary" : "ghost"} aria-pressed={view === "messages"} onClick={() => setView("messages")}>
                逐封
              </Button>
              <Button size="sm" variant={view === "threads" ? "secondary" : "ghost"} aria-pressed={view === "threads"} onClick={() => setView("threads")}>
                對話串
              </Button>
            </div>
            )}
            {!filters.all_folders && !inBackup ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={cloudDisabled}
                icon={<CheckCheck aria-hidden="true" size={14} />}
                onClick={() => void markAllRead()}
              >
                全部標已讀
              </Button>
            ) : null}
          </div>
          {inBackup && usage ? (
            <div data-backup-usage="" className="flex flex-col gap-1 rounded-[var(--radius)] border border-[var(--border)] px-3 py-2">
              <p className="m-0 text-sm text-[var(--text)]">
                備份共 {formatBackupSize(usage.total_bytes)} · {formatCount(usage.total_count)} 封
              </p>
              <p className="m-0 text-xs text-[var(--text-3)]">
                其中雲端已刪除 {formatCount(usage.gone_count)} 封,共 {formatBackupSize(usage.gone_bytes)}(可清理)
              </p>
              <div>
                <Button size="sm" disabled={usage.gone_count === 0} onClick={() => setPurgeOpen(true)}>
                  清理備份
                </Button>
              </div>
            </div>
          ) : null}
          {view === "threads" && searching ? (
            <p className="m-0 text-xs text-[var(--text-3)]">搜尋時逐封列出。</p>
          ) : null}
          {!threadMode && !inBackup ? (
            <MailBatchBar
              ids={[...checked]}
              purgeOnDelete={items.some((one) => checked.has(one.id) && one.folder === TRASH)}
              folders={shownFolders}
              currentFolder={filters.all_folders ? null : folder}
              disabled={cloudDisabled}
              onDone={afterBatch}
              onError={(err) => {
                handleError(err);
              }}
              onClear={() => setChecked(new Set())}
            />
          ) : null}
          <div data-mail-scroll="" className="min-h-0 flex-1 overflow-y-auto">
          <MailList
            items={items}
            selectedId={openId}
            cursorId={cursorId}
            checked={threadMode || inBackup ? undefined : checked}
            onToggleCheck={(id) =>
              setChecked((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onToggleAll={() =>
              setChecked((prev) =>
                items.every((one) => prev.has(one.id)) ? new Set() : new Set(items.map((one) => one.id)),
              )
            }
            showRecipients={folder === SENT && !filters.all_folders}
            loading={listLoading}
            hasMore={nextBefore !== null}
            emptyText={searching ? "沒有符合的信。" : `${folderLabel}是空的。`}
            onOpen={openRow}
            onLoadMore={() => {
              if (nextBefore !== null && !listLoading) void loadList(nextBefore);
            }}
          />
          </div>
        </div>

        <div
          ref={readerPaneRef}
          data-mail-pane="reader"
          className={"min-w-0 flex-1 lg:min-h-0 lg:overflow-y-auto lg:pl-3 " + (pane === "reader" ? "" : "hidden md:block")}
        >
          {compose ? (
            <MailComposer
              key={composeSeq}
              ref={composerRef}
              mode={compose}
              selfEmail={status.email}
              disabled={cloudDisabled}
              onClose={() => {
                leaveCompose();
              }}
              onDirtyChange={(dirty) => {
                composeDirty.current = dirty;
                if (dirty) composeEverDirty.current = true;
              }}
              onSent={(refused) => {
                composeDirty.current = false;
                setCompose(null);
                setRefusedWarning(refused.length > 0 ? refused : null);
                void loadFolders();
                if (folder === SENT) later(() => void loadListRef.current(), PENDING_RELOAD_MS);
              }}
              onScheduled={() => {
                composeDirty.current = false;
                setCompose(null);
                setScheduledRefresh((n) => n + 1);
              }}
              onError={(err) => {
                handleError(err, true);
              }}
            />
          ) : scheduledOpen ? (
            <ScheduledList refreshKey={scheduledRefresh} onBack={() => setScheduledOpen(false)} />
          ) : thread ? (
            thread.items === null ? (
              loadingWithBack
            ) : (
              <MailThreadView items={thread.items} expandedId={openId} onExpand={(one) => void showMessage(one)}>
                {reader}
              </MailThreadView>
            )
          ) : reader ? (
            reader
          ) : (
            <p className="m-0 text-sm text-[var(--text-3)]">點左邊的信來讀。</p>
          )}
        </div>
        </>
        )}
      </div>

      <BackupPurgeDialog
        open={purgeOpen}
        onClose={() => setPurgeOpen(false)}
        onPurged={() => {
          void loadUsage();
          void loadFolders();
          void loadListRef.current();
          // 開在讀信區的那封如果被清掉了,關掉讀信區(不留一封已經不存在的信)
          const openNow = openIdRef.current;
          if (openNow !== null) {
            mailbox.message(openNow).catch((err: unknown) => {
              if (alive.current && openIdRef.current === openNow && mailErrorCode(err) === "MAILBOX_NOT_FOUND") {
                closeReader();
              }
            });
          }
        }}
      />
      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
